import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { environment } from 'environments/environment';
import { BehaviorSubject, firstValueFrom, Observable, Subject, take } from 'rxjs';
import { ActionBase } from '@app/actions/action.base';
import { ActionName } from '@app/actions/action.types';
import { mapNotificationDataToAction } from '@app/actions/notification-to-action.util';
import {
  PushNotifications,
  PushNotificationSchema,
  Token,
  ActionPerformed,
} from '@capacitor/push-notifications';
import { App } from '@capacitor/app';
import { Capacitor, PermissionState, PluginListenerHandle } from '@capacitor/core';
import { DeviceType } from '../types/common/device.types';
import { FeedbackSandbox } from '../sandboxes/feedback.sandbox';
import { Route } from '@app/core/enums/route.enum';
import { AnalyticsService } from './analytics.service';
import { AnalyticsEvents } from '../enums/analytics-events.enum';
import { AndroidSettings, IOSSettings, NativeSettings } from 'capacitor-native-settings';
import { ToastSandbox } from '../sandboxes/toast.sandbox';
import { NotificationData } from '../types/common/notification.types';
import { NotificationActions, NotificationTypes } from '../enums/notification.enum';
import { AuthService } from '@auth0/auth0-angular';
import { ReportingService } from './reporting.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationService extends ActionBase {
  private readonly http = inject(HttpClient);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly toastSandbox = inject(ToastSandbox);
  private readonly feedbackSandbox = inject(FeedbackSandbox);
  private readonly reportingService = inject(ReportingService);

  // Inject AuthService directly since injecting AuthenticationService causes circular dependencies
  // To refactor: AuthenticationService should not import all these services to cleanup on logout,
  //   it should just broadcast an event that other services can listen to in order to do their cleanup
  private readonly authenticationService = inject(AuthService);

  private readonly url = `${environment.url.api}/notification`;

  private permissionStateResumeListener: PluginListenerHandle;

  token$: Subject<string> = new BehaviorSubject(null);

  async initialize(): Promise<void> {
    const platform = Capacitor.getPlatform();

    if (platform === DeviceType.Web) {
      return;
    }

    this.addRegistrationListener();
  }

  addRegistrationListener(): void {
    PushNotifications.addListener('registration', async (token: Token) => {
      const isAuthenticated = await firstValueFrom(this.authenticationService.isAuthenticated$);

      if(isAuthenticated) {
        this.registerDeviceToken(token.value).pipe(take(1)).subscribe();
        this.token$.next(token.value);
      }
    });
  }

  addPushNotificationReceivedListener(): void {
    PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
      const actionName = notification?.data?.actionName;

      if (!actionName) {
        return;
      }

      if (actionName === ActionName.FeedbackWeek) {
        return this.feedbackSandbox.openWeekFeedbackModalForNotification();
      } else if (actionName === ActionName.NewChatMessage && this.router.url.includes("chat")) {
        return;
      }

      this.analyticsService.logEvent(AnalyticsEvents.Notification, {
        type: NotificationTypes.InApp,
        action: NotificationActions.Received,
        name: notification?.data?.analyticsType,
      });

      this.toastSandbox.showLocalNotification({
        header: notification.title,
        message: notification.body,
        clickFn: this.getLocalNotificationClickFn(notification.data),
      });
    });
  }

  addPushNotificationActionPerformedListener(): void {
    PushNotifications.addListener('pushNotificationActionPerformed', (notification: ActionPerformed) => {
      this.handleClickOnNotification(notification?.notification?.data);
      this.analyticsService.logEvent(AnalyticsEvents.Notification, {
        type: NotificationTypes.Push,
        action: NotificationActions.Opened,
        name: notification?.notification?.data?.analyticsType,
      });
    });
  }

  async registerNotifications(): Promise<boolean> {
    let permStatus = await PushNotifications.checkPermissions();

    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions();
    }

    if (permStatus.receive !== 'granted') {
      return false;
    }

    await PushNotifications.register();

    const platform = Capacitor.getPlatform();
    if (platform === DeviceType.Android) {
      await this.addNotificationChannel();
    }

    return true;
  }

  async getPushNotificationPermissionState(): Promise<PermissionState> {
    if (!Capacitor.isNativePlatform()) {
      return 'prompt';
    }
    const permissionStatus = await PushNotifications.checkPermissions();
    return permissionStatus.receive;
  }

  async requestPushNotificationPermission(): Promise<PermissionState> {
    if (!Capacitor.isNativePlatform()) {
      return 'prompt';
    }
    const permissionStatus = await PushNotifications.requestPermissions();
    return permissionStatus.receive;
  }

  async registerAndReportPermissionState(): Promise<void> {
    await this.registerNotifications();
    const state = await this.getPushNotificationPermissionState();
    this.reportingService.reportPushNotificationPermissionState(state).subscribe();
  }

  async addPermissionStateResumeListener(): Promise<void> {
    this.permissionStateResumeListener = await App.addListener('resume', async () => {
      const state = await this.getPushNotificationPermissionState();
      this.reportingService.reportPushNotificationPermissionState(state).subscribe();
    });
  }

  cleanUpListeners(): void {
    if (this.permissionStateResumeListener) {
      this.permissionStateResumeListener.remove();
      this.permissionStateResumeListener = null;
    }
  }

  addNotificationChannel(): void {
    PushNotifications.createChannel({
      id: 'square_channel_id',
      name: 'Notification channel for Square',
      importance: 4,
    });
  }

  registerDeviceToken(deviceToken: string): Observable<void> {
    return this.http.post<void>(`${this.url}/register/user/me`, { deviceToken });
  }

  async unregisterNotifications(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      await PushNotifications.unregister();
    }
  }

  openPushNotificationSettings(): Promise<{ status: boolean }> {
    return NativeSettings.open({
      optionAndroid: AndroidSettings.ApplicationDetails,
      optionIOS: IOSSettings.App
    });
  }

  private getLocalNotificationClickFn(notificationData: NotificationData): () => void | null {
    return () => {
      this.analyticsService.logEvent(AnalyticsEvents.Notification, {
        type: NotificationTypes.InApp,
        action: NotificationActions.Clicked,
        name: notificationData.analyticsType,
      });
      this.handleClickOnNotification(notificationData);
    };
  }

  private handleClickOnNotification(notificationData: NotificationData) {
    if (notificationData?.actionName) {
      this.onActionClick(mapNotificationDataToAction(notificationData));
    }
  }

  /**
   * Notifications have no route context, so route-based actions are navigated
   * absolutely under the dashboard, which mounts `actionRoutes` like the dashboard
   * and inbox tasks do.
   */
  protected override navigateToAction(commands: string[], queryParams?: Params): void {
    this.router.navigate([Route.Home, Route.Dashboard, ...commands], { queryParams });
  }
}
